import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { firebaseApp, db } from "@healthdesk/shared-services";
import firebase from "firebase/compat/app";
import { medicationService } from "@healthdesk/shared-services";
import { adherenceService } from "@healthdesk/shared-services";

/** @type {import('react').Context<import('./authContext.types').AuthContextValue | undefined>} */
export const AuthContext = React.createContext(undefined);

/** Platform roles that do not use patient billing / medication subcollections under their own uid. */
const STAFF_ROLES_SKIP_PATIENT_FIRESTORE = new Set([
  "llm-manager-global",
  "llm-manager-regional",
  "chartmind-admin",
  "chartmind-kijabe",
  "dscr-admin",
  "system-medical-admin",
  "lotsog",
]);
const FIRESTORE_PERMISSION_DENIED_CODE = "permission-denied";

/**
 * Skip subscriptions/listings/invoices/medication/tracking listeners for staff; avoids
 * permission-denied noise when rules are owner-only and during account switches.
 */
function shouldSkipPatientScopedFirestoreListeners(roleFromDocOrStripe, tokenClaims) {
  if (tokenClaims?.globalLlmManager === true) return true;
  const r = typeof roleFromDocOrStripe === "string" ? roleFromDocOrStripe : "";
  return STAFF_ROLES_SKIP_PATIENT_FIRESTORE.has(r);
}

function isPermissionDeniedError(error) {
  return error?.code === FIRESTORE_PERMISSION_DENIED_CODE;
}

/**
 * @param {{ children: import('react').ReactNode; onAuthenticated?: () => void }} props
 */
export const AuthProvider = ({ children, onAuthenticated }) => {
  const [user, setUser] = useState(
    /** @type {import('./authContext.types').AuthCompatUser} */ (undefined)
  );
  // NOTE: "subscription" is misleadingly named - it's actually the user's ROLE (patient, facility, etc.)
  // This is for backwards compatibility. The actual Stripe subscription data is in "subscriptionData"
  const [subscription, setSubscription] = useState(
    /** @type {string | null | undefined} */ (undefined)
  );
  // The actual Stripe subscription object with status, created date, etc.
  const [subscriptionData, setSubscriptionData] = useState(
    /** @type {import('./authContext.types').AuthSubscriptionDoc | null | undefined} */ (
      undefined
    )
  );
  const [dailyPass, setDailyPass] = useState(
    /** @type {unknown} */ (undefined)
  );
  const [userData, setUserData] = useState(
    /** @type {import('./authContext.types').AuthUserDataRow | null | undefined} */ (
      undefined
    )
  );
  const [listings, setListings] = useState(
    /** @type {Record<string, Record<string, unknown>>} */ ({})
  );
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState(
    /** @type {import('./authContext.types').AuthInvoiceDoc[]} */ ([])
  );
  // Medications cache
  const [medications, setMedications] = useState(
    /** @type {import('./authContext.types').AuthMedicationRow[]} */ ([])
  );
  const [reminderStatuses, setReminderStatuses] = useState(
    /** @type {Map<string, { enabled?: boolean; times?: string[]; timezone?: string }>} */ (
      new Map()
    )
  );
  const [medicationsLoading, setMedicationsLoading] = useState(false);

  // Tracking data cache - SIMPLE APPROACH for launch
  // 6 weeks: Fast loading, good for charts, covers most user patterns
  // When we scale to thousands of users, we can optimize further
  const [trackingData, setTrackingData] = useState(
    /** @type {import('./authContext.types').AuthTrackingDataState} */ ({
      dashboardSummary: null,
      calendarDataByMedication: new Map(),
      loaded: false,
      loading: false,
    })
  );
  /** @type {import('react').MutableRefObject<(() => void) | null>} */
  const unsubRef = useRef(null);
  /** @type {import('react').MutableRefObject<(() => void) | null>} */
  const dailyPassUnsubRef = useRef(null);
  /** @type {import('react').MutableRefObject<(() => void) | null>} */
  const subscriptionUnsubRef = useRef(null);
  /** @type {import('react').MutableRefObject<(() => void) | null>} */
  const listingsUnsubRef = useRef(null);
  /** @type {import('react').MutableRefObject<(() => void) | null>} */
  const invoicesUnsubRef = useRef(null);
  /** @type {import('react').MutableRefObject<(() => void) | null>} */
  const medicationUnsubRef = useRef(null);
  /** @type {import('react').MutableRefObject<(() => void) | null>} */
  const reminderUnsubRef = useRef(null);
  /** @type {import('react').MutableRefObject<(() => void) | null>} */
  const trackingUnsubRef = useRef(null);

  const detachPatientScopedFirestoreListeners = () => {
    if (subscriptionUnsubRef.current) {
      subscriptionUnsubRef.current();
      subscriptionUnsubRef.current = null;
    }
    if (listingsUnsubRef.current) {
      listingsUnsubRef.current();
      listingsUnsubRef.current = null;
    }
    if (invoicesUnsubRef.current) {
      invoicesUnsubRef.current();
      invoicesUnsubRef.current = null;
    }
    if (medicationUnsubRef.current) {
      medicationUnsubRef.current();
      medicationUnsubRef.current = null;
    }
    if (reminderUnsubRef.current) {
      reminderUnsubRef.current();
      reminderUnsubRef.current = null;
    }
    if (trackingUnsubRef.current) {
      trackingUnsubRef.current();
      trackingUnsubRef.current = null;
    }
  };

  const getData = (uid) => {
    return db
      .collection("users")
      .doc(uid)
      .onSnapshot(
        (userDocSnap) => {
          const data = userDocSnap.data();
          // Effective role: base role or first system/org group-scoped role (plan: invited roles are group-scoped only)
          const effectiveRole = data?.role || data?.systemGroupRoles?.[0]?.role;
          const dataWithRole = data ? { ...data, role: effectiveRole } : data;
          setUserData(dataWithRole);
          if (effectiveRole) {
            setSubscription(effectiveRole);
          }
          // If role or claims later show staff / global LLM manager, drop patient listeners (do not attach here — onAuth owns attach).
          firebaseApp.auth().currentUser?.getIdTokenResult().then((token) => {
            const roleForPatient =
              (effectiveRole || token?.claims?.stripeRole || "").toString() || null;
            const skip = shouldSkipPatientScopedFirestoreListeners(
              roleForPatient,
              token?.claims
            );
            if (skip && subscriptionUnsubRef.current) {
              detachPatientScopedFirestoreListeners();
              setSubscriptionData(null);
              setListings({});
              setInvoices([]);
              setMedications([]);
              setReminderStatuses(new Map());
              setMedicationsLoading(false);
              clearTrackingData();
            }
          }).catch(() => {});
        },
        (error) => {
          // Gracefully handle permission errors during auth state transitions
          console.warn('[Auth] User data snapshot error (may recover on auth refresh):', error.code);
        }
      );
  };

  const checkSubscriptions = (uid) => {
    return db
      .collection("users")
      .doc(uid)
      .collection("subscriptions")
      .onSnapshot(
        (snapshot) => {
          // Find the most recent subscription document
          const subscriptions = snapshot.docs.map((doc) =>
            /** @type {import('./authContext.types').AuthSubscriptionDoc} */ ({
              id: doc.id,
              ...doc.data(),
            })
          );

          if (subscriptions.length > 0) {
            // Sort by created date (most recent first) and take the first one
            const latestSubscription = subscriptions.sort(
              (a, b) => (b.created || 0) - (a.created || 0)
          )[0];

          setSubscriptionData(latestSubscription);
        } else {
          setSubscriptionData(null);
        }
      },
      (error) => {
        console.warn('[Auth] Subscriptions snapshot error:', error.code);
      }
    );
  };

  const checkListings = (uid) => {
    return db
      .collection("users")
      .doc(uid)
      .collection("listings")
      .onSnapshot(
        (snapshot) => {
          const listingsData = {};
          snapshot.forEach((doc) => {
            const listing = doc.data();
            listingsData[doc.id] = {
              id: doc.id,
              type: listing.type,
              status: listing.status || "pending",
              active: listing.active || false,
              createdAt: listing.createdAt,
              expirationDate: listing.expirationDate,
              zipCode: doc.id,
              // Fields from original zipSubscriptions (featured listings)
              isWinner: listing.type === "featured" ? true : undefined,
              isPaid:
                listing.type === "featured"
                  ? listing.status === "active"
                  : undefined,
              endDate: listing.endDate,
              winningBid: listing.winningBid,
              // Include any other fields that might exist
              amount_paid: listing.amount_paid,
            };
          });
          setListings(
            /** @type {Record<string, Record<string, unknown>>} */ (listingsData)
          );
        },
        (error) => {
          console.warn('[Auth] Listings snapshot error:', error.code);
        }
      );
  };

  const checkInvoices = (uid) => {
    return db
      .collection("users")
      .doc(uid)
      .collection("invoices")
      .where("status", "in", ["open", "pending"])
      .onSnapshot(
        (snapshot) => {
          const invoicesData = snapshot.docs.map((doc) => doc.data());
          setInvoices(invoicesData);
        },
        (error) => {
          console.warn('[Auth] Invoices snapshot error:', error.code);
        }
      );
  };

  // Load medications and reminder statuses with real-time snapshots
  const setupMedicationSnapshots = (uid) => {
    if (!uid) return;

    setMedicationsLoading(true);

    // Set up medication snapshot listener
    medicationUnsubRef.current = db
      .collection("users")
      .doc(uid)
      .collection("medications")
      .orderBy("createdAt", "desc")
      .onSnapshot(
        (snapshot) => {
          const meds = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            // Convert Firestore Timestamps to JS Dates
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate(),
          }));
          setMedications(meds);
          setMedicationsLoading(false);
        },
        (error) => {
          if (isPermissionDeniedError(error)) {
            console.warn("[Auth] Medication snapshot permission denied; skipping medication sync.");
          } else {
            console.error("Error in medication snapshot:", error);
          }
          setMedications([]);
          setMedicationsLoading(false);
        }
      );

    // Set up reminder status snapshot listener
    reminderUnsubRef.current = db
      .collection("users")
      .doc(uid)
      .collection("medicationReminders")
      .onSnapshot(
        (snapshot) => {
          const reminderMap = new Map();
          snapshot.docs.forEach((doc) => {
            const data = doc.data();
            reminderMap.set(data.medicationId, {
              enabled: data.enabled,
              times: data.times || [],
              timezone: data.timezone,
            });
          });
          setReminderStatuses(reminderMap);
        },
        (error) => {
          if (isPermissionDeniedError(error)) {
            console.warn("[Auth] Reminder snapshot permission denied; skipping reminder sync.");
          } else {
            console.error("Error in reminder snapshot:", error);
          }
          setReminderStatuses(new Map());
        }
      );
  };

  // Legacy function for backwards compatibility - now just calls setupMedicationSnapshots
  const loadMedications = (uid) => {
    return setupMedicationSnapshots(uid);
  };

  // Set up smart tracking data snapshot listener
  const setupTrackingDataSnapshot = (uid) => {
    if (!uid) return;

    // Calculate date range for recent tracking data (6 weeks)
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 42);

    // Format dates as YYYY-MM-DD for document IDs
    const startDateId = startDate.toISOString().slice(0, 10);
    const endDateId = endDate.toISOString().slice(0, 10);

    // Set up snapshot listener for recent tracking data
    trackingUnsubRef.current = db
      .collection("users")
      .doc(uid)
      .collection("medicationTracking")
      .where(firebase.firestore.FieldPath.documentId(), ">=", startDateId)
      .where(firebase.firestore.FieldPath.documentId(), "<=", endDateId)
      .onSnapshot(
        (snapshot) => {
          // Only trigger reload if we already have data loaded and this is a real change
          if (trackingData.loaded && !snapshot.metadata.fromCache) {
            // Invalidate and reload tracking data
            setTrackingData((prev) => ({ ...prev, loaded: false }));
            // The existing loadTrackingData will be triggered by the useEffect
          }
        },
        (error) => {
          if (isPermissionDeniedError(error)) {
            console.warn("[Auth] Tracking snapshot permission denied; skipping tracking sync.");
          } else {
            console.error("Error in tracking data snapshot:", error);
          }
        }
      );
  };

  // Medication CRUD operations
  const addMedication = async (medicationData) => {
    const uid = user?.uid;
    if (!uid) throw new Error("User not authenticated");

    try {
      const newMedication = await medicationService.createMedication(
        uid,
        medicationData
      );
      setMedications((prev) => [newMedication, ...prev]);

      // Invalidate tracking data cache so new medication gets included
      setTrackingData((prev) => ({ ...prev, loaded: false }));

      return newMedication;
    } catch (error) {
      console.error("Error adding medication:", error);
      throw error;
    }
  };

  const updateMedication = async (medicationId, medicationData) => {
    const uid = user?.uid;
    if (!uid) throw new Error("User not authenticated");

    try {
      await medicationService.updateMedication(
        uid,
        medicationId,
        medicationData
      );
      setMedications((prev) =>
        prev.map((med) =>
          med.id === medicationId ? { ...med, ...medicationData } : med
        )
      );

      // Invalidate tracking data cache so changes get reflected
      setTrackingData((prev) => ({ ...prev, loaded: false }));
    } catch (error) {
      console.error("Error updating medication:", error);
      throw error;
    }
  };

  const deleteMedication = async (medicationId) => {
    const uid = user?.uid;
    if (!uid) throw new Error("User not authenticated");

    try {
      await medicationService.deleteMedication(uid, medicationId);
      setMedications((prev) => prev.filter((med) => med.id !== medicationId));
      // Also remove from reminder statuses
      setReminderStatuses((prev) => {
        const newMap = new Map(prev);
        newMap.delete(medicationId);
        return newMap;
      });

      // Invalidate tracking data cache so deleted medication gets removed
      setTrackingData((prev) => ({ ...prev, loaded: false }));
    } catch (error) {
      console.error("Error deleting medication:", error);
      throw error;
    }
  };

  // Refresh reminder statuses after adherence changes
  const refreshReminderStatuses = async () => {
    const uid = user?.uid;
    if (!uid) return;

    try {
      const reminders = await adherenceService.getUserReminders(uid);
      const reminderMap = new Map();
      for (const row of /** @type {readonly unknown[]} */ (reminders)) {
        const reminder =
          /** @type {{ medicationId?: string; enabled?: boolean; times?: string[]; timezone?: string }} */ (
            row
          );
        reminderMap.set(reminder.medicationId, {
          enabled: reminder.enabled,
          times: reminder.times || ["08:00"],
          timezone: reminder.timezone,
        });
      }
      setReminderStatuses(reminderMap);
    } catch (error) {
      console.error("Error refreshing reminder statuses:", error);
    }
  };

  // Helper function to get last 7 days
  const getLast7Days = (dailyData) => {
    const days = [];
    const today = new Date();

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateString = date.toISOString().split("T")[0];

      // Find data for this date
      const dayData = dailyData.find((d) => d.date === dateString);
      if (dayData) {
        days.push(dayData);
      }
    }

    return days;
  };

  // Load tracking data - LAUNCH COMPROMISE: 6 weeks for performance + accuracy balance
  // Fast for new users, accurate for recent trends, simple architecture
  const loadTrackingData = useCallback(
    async (daysToLoad = 42) => {
      if (!user?.uid || trackingData.loading || trackingData.loaded) {
        return;
      }

      // Process ALL medications, not just those with alerts enabled
      // The filtering should happen at display time, not data loading time
      const allMeds = medications;

      if (allMeds.length === 0) {
        setTrackingData((prev) => ({ ...prev, loaded: true }));
        return;
      }

      try {
        setTrackingData((prev) => ({ ...prev, loading: true }));

        // Get tracking data for all medications
        const calendarDataByMedication = new Map();

        await Promise.all(
          allMeds.map(async (med, index) => {
            try {
              const dailyData = await adherenceService.getDailyTrackingData(
                med.id,
                daysToLoad
              );

              // Get last 7 days for compact view
              const last7Days = getLast7Days(dailyData || []);

              calendarDataByMedication.set(med.id, {
                dailyData: dailyData || [],
                isEmpty: !dailyData || dailyData.length === 0,
                medicationName: med.name,
                last7Days,
              });
            } catch (error) {
              console.error(
                `Error loading tracking data for ${med.name}:`,
                error
              );
              calendarDataByMedication.set(med.id, {
                dailyData: [],
                isEmpty: true,
                medicationName: med.name,
                last7Days: [],
              });
            }
          })
        );

        // Process dashboard summary
        const dashboardSummary = processDashboardSummary(
          calendarDataByMedication,
          allMeds
        );

        setTrackingData({
          dashboardSummary,
          calendarDataByMedication,
          loaded: true,
          loading: false,
        });
      } catch (error) {
        console.error("Error loading tracking data:", error);
        setTrackingData((prev) => ({
          ...prev,
          loading: false,
          loaded: true, // Prevent infinite retry
        }));
      }
    },
    [
      user?.uid,
      trackingData.loading,
      trackingData.loaded,
      medications, // Use full array to avoid stale closure issues
    ]
  );

  // REMOVED complex lifetime stats loading - keeping it simple for launch
  // When we have thousands of users, we can add back sophisticated optimization

  // Process dashboard summary - moved from hook to context
  const processDashboardSummary = (calendarDataByMedication, medications) => {
    // For dashboard summary, we only calculate stats for medications with alerts enabled
    const activeMeds = medications.filter((med) => med.adherence?.enabled);

    let totalPerfectDays = 0;
    let totalGoodDays = 0;
    let totalPartialDays = 0;
    let totalPoorDays = 0;
    let totalMissedDays = 0;
    let totalResponsesGiven = 0;
    let totalMedicationsTaken = 0; // FIXED: Count actual medications taken, not days
    let adherenceScores = [];
    let allStreaks = [];

    calendarDataByMedication.forEach((calendarData, medId) => {
      if (calendarData.isEmpty || !calendarData.dailyData) {
        return;
      }

      let perfectDays = 0;
      let goodDays = 0;
      let partialDays = 0;
      let poorDays = 0;
      let missedDays = 0;
      let responsesGiven = 0;

      calendarData.dailyData.forEach((day) => {
        const adherenceRate = day.adherenceRate || 0;
        const takenEntries =
          day.entries?.filter((e) => e.status === "taken").length || 0;
        responsesGiven += takenEntries;

        // FIXED: Count actual medications taken for this medication
        if (day.isRealTrackingDay && day.expectedReminders > 0) {
          totalMedicationsTaken += takenEntries;
        }

        if (day.entries && day.entries.length > 0) {
          if (adherenceRate >= 1.0) {
            perfectDays++;
          } else if (adherenceRate >= 0.8) {
            goodDays++;
          } else if (adherenceRate >= 0.5) {
            partialDays++;
          } else if (adherenceRate > 0) {
            poorDays++;
          } else {
            missedDays++;
          }
        }
      });

      // Calculate adherence score
      let adherenceScore = 0;
      let totalDosesTaken = 0;
      let totalDosesExpected = 0;

      calendarData.dailyData.forEach((day) => {
        // FIXED: Only count real tracking days, not heatmap filler days
        if (day.isRealTrackingDay && day.expectedReminders > 0) {
          const expectedCount = day.expectedReminders;
          const takenCount = day.entries
            ? day.entries.filter((e) => e.status === "taken").length
            : 0;

          totalDosesTaken += takenCount;
          totalDosesExpected += expectedCount;
        }
      });

      if (totalDosesExpected > 0) {
        adherenceScore = Math.round(
          (totalDosesTaken / totalDosesExpected) * 100
        );
      }

      // Calculate streaks - both current and all historical consecutive streaks
      const daysWithData = calendarData.dailyData
        .filter((day) => day.isRealTrackingDay && day.expectedReminders > 0)
        .sort(
          (a, b) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
        ); // Sort chronologically for streak analysis

      // Calculate current streak (from most recent day backwards)
      let currentStreak = 0;
      const reverseDays = [...daysWithData].reverse();
      for (const day of reverseDays) {
        if (day.adherenceRate >= 1.0) {
          currentStreak++;
        } else {
          break;
        }
      }

      // Calculate ALL consecutive streaks throughout history
      const allConsecutiveStreaks = [];
      let currentConsecutiveStreak = 0;

      for (const day of daysWithData) {
        if (day.adherenceRate >= 1.0) {
          currentConsecutiveStreak++;
        } else {
          if (currentConsecutiveStreak > 0) {
            allConsecutiveStreaks.push(currentConsecutiveStreak);
            currentConsecutiveStreak = 0;
          }
        }
      }
      // Don't forget the last streak if the data ends on a perfect day
      if (currentConsecutiveStreak > 0) {
        allConsecutiveStreaks.push(currentConsecutiveStreak);
      }

      // Add to totals
      totalPerfectDays += perfectDays;
      totalGoodDays += goodDays;
      totalPartialDays += partialDays;
      totalPoorDays += poorDays;
      totalMissedDays += missedDays;
      totalResponsesGiven += responsesGiven;

      if (adherenceScore > 0) {
        adherenceScores.push(adherenceScore);
      }

      // Store both current streak and all historical streaks
      allStreaks.push({
        currentStreak,
        allConsecutiveStreaks,
        medicationId: medId,
      });
    });

    // FIXED: Use total dose calculation (33%) instead of average percentages (25%)
    // Calculate overall adherence as total taken / total expected across ALL medications
    let grandTotalTaken = 0;
    let grandTotalExpected = 0;

    calendarDataByMedication.forEach((calendarData, medId) => {
      if (calendarData.isEmpty || !calendarData.dailyData) return;

      calendarData.dailyData.forEach((day) => {
        // FIXED: Only count real tracking days for overall calculation
        if (day.isRealTrackingDay && day.expectedReminders > 0) {
          const expectedCount = day.expectedReminders;
          const takenCount = day.entries
            ? day.entries.filter((e) => e.status === "taken").length
            : 0;

          grandTotalTaken += takenCount;
          grandTotalExpected += expectedCount;
        }
      });
    });

    const averageAdherence =
      grandTotalExpected > 0
        ? Math.round((grandTotalTaken / grandTotalExpected) * 100)
        : 0;

    // Filter streaks for medications that have actual tracking data
    const activeStreakData = allStreaks.filter((streakData) => {
      const medData = calendarDataByMedication.get(streakData.medicationId);
      const hasTrackingData =
        medData &&
        !medData.isEmpty &&
        medData.dailyData.some((day) => day.entries && day.entries.length > 0);
      return hasTrackingData;
    });

    // Current streak is the minimum across all active medications (all meds must be consistent)
    const currentStreaks = activeStreakData.map((data) => data.currentStreak);
    const currentStreak =
      currentStreaks.length > 0 ? Math.min(...currentStreaks) : 0;

    // Best streak is the maximum of ALL consecutive streaks throughout history across all medications
    const allHistoricalStreaks = allStreaks.flatMap(
      (data) => data.allConsecutiveStreaks
    );
    const bestStreak =
      allHistoricalStreaks.length > 0 ? Math.max(...allHistoricalStreaks) : 0;

    // Generate encouragement message
    let message = "";
    if (totalResponsesGiven > 0) {
      const firstDoseDate = Array.from(calendarDataByMedication.values())
        .flatMap((data) => data.dailyData || [])
        .filter((day) => day.adherenceRate > 0)
        .map((day) => new Date(day.date))
        .sort((a, b) => a.getTime() - b.getTime())[0];

      const daysSinceStart = firstDoseDate
        ? Math.ceil(
            (Date.now() - firstDoseDate.getTime()) / (1000 * 60 * 60 * 24)
          )
        : 1;

      if (averageAdherence >= 90) {
        message =
          daysSinceStart >= 30
            ? "Outstanding! Your consistent routine over the past month is paying off."
            : daysSinceStart >= 14
            ? "Excellent work! You're building an amazing medication habit."
            : "Fantastic start! You're already crushing your medication routine.";
      } else if (averageAdherence >= 75) {
        message =
          daysSinceStart >= 21
            ? "Solid work! You've built a strong habit."
            : "Great job! You're staying consistent with your medications.";
      } else if (averageAdherence >= 50) {
        message =
          bestStreak >= 5
            ? "You've had some great streaks! Let's get back to that consistent rhythm."
            : "You're making progress! Consider adding more reminder times or adjusting the schedule to improve.";
      } else {
        message =
          daysSinceStart <= 3
            ? "Every start is a victory! Building a medication routine takes time - you've got this."
            : bestStreak > 0
            ? "You can do this! Let's get back to that streak you had going."
            : "Starting is the hardest part, and you've already begun! Try setting more frequent reminders to build the habit.";
      }
    }

    return {
      averageAdherence,
      totalTaken: totalMedicationsTaken, // TODO(SCALE): When users have 6+ months of data, consider loading full history for lifetime accuracy
      totalMissed: totalPartialDays + totalPoorDays + totalMissedDays,
      bestStreak, // TODO(SCALE): May not reflect true lifetime best if user has data beyond 6-week window
      currentStreak,
      badges: [],
      message,
      totalMedications: activeMeds.length,
    };
  };

  // Clear tracking data when needed
  const clearTrackingData = useCallback(() => {
    setTrackingData({
      dashboardSummary: null,
      calendarDataByMedication: new Map(),
      loaded: false,
      loading: false,
    });
  }, []);

  // Force reset tracking data
  const forceResetTrackingData = () => {
    setTrackingData({
      dashboardSummary: null,
      calendarDataByMedication: new Map(),
      loaded: false,
      loading: false,
    });
  };

  // Sync pending email verification when user logs in
  // This handles the case where email was verified but Firestore update failed
  useEffect(() => {
    const syncPendingEmail = async () => {
      if (!user || !userData) return;
      
      // Check if there's a pending email that matches Firebase Auth email
      if (userData.pendingEmail && user.email && userData.pendingEmail === user.email) {
        try {
          await db.collection("users").doc(user.uid).update({
            email: user.email,
            pendingEmail: firebase.firestore.FieldValue.delete(),
            emailChangeRequested: firebase.firestore.FieldValue.delete(),
          });
        } catch (err) {
          console.error("Failed to sync email:", err);
        }
      }
    };

    syncPendingEmail();
  }, [user, userData]);

  useEffect(() => {
    let authListener = firebaseApp.auth().onAuthStateChanged(async (user) => {
      setUser(user);

      if (user) {
        try {
          onAuthenticated?.();
        } catch (e) {
          console.warn("[Auth] onAuthenticated callback failed (non-blocking):", e);
        }
        // Force refresh so custom claims (e.g. globalLlmManager) match Firestore before attaching
        // patient-scoped listeners; stale cached tokens caused permission-denied on owner-only paths.
        const decodedToken = await firebaseApp.auth().currentUser?.getIdTokenResult(true).catch(() => null);

        // Account switch: drop previous user doc + patient-scoped listeners before re-attaching
        if (unsubRef.current) {
          unsubRef.current();
          unsubRef.current = null;
        }
        detachPatientScopedFirestoreListeners();

        // Set up Firestore listener so userData stays in sync
        unsubRef.current = getData(user.uid);

        /** @type {string | null} */
        let roleForPatientListeners = null;
        // Single user-doc read: set userData + role so DashboardPage doesn't refetch
        try {
          const userDoc = await db.collection("users").doc(user.uid).get();
          const docData = userDoc.exists ? userDoc.data() : null;
          const effectiveRole = docData?.role || docData?.systemGroupRoles?.[0]?.role;
          const role = effectiveRole || decodedToken?.claims?.stripeRole;
          roleForPatientListeners =
            effectiveRole || decodedToken?.claims?.stripeRole || null;
          if (effectiveRole) {
            setSubscription(effectiveRole);
          } else if (role) {
            setSubscription(role);
          }
          const dataWithRole = docData ? { ...docData, role: role || effectiveRole } : null;
          setUserData(dataWithRole);
        } catch (error) {
          console.warn('Failed to fetch initial userData, using token claims:', error);
          const role = decodedToken?.claims?.stripeRole;
          roleForPatientListeners = role || null;
          setSubscription(role);
        }

        const skipPatientFirestore = shouldSkipPatientScopedFirestoreListeners(
          roleForPatientListeners,
          decodedToken?.claims
        );

        if (!skipPatientFirestore) {
          subscriptionUnsubRef.current = checkSubscriptions(user.uid);
          listingsUnsubRef.current = checkListings(user.uid);
          invoicesUnsubRef.current = checkInvoices(user.uid);
          setupMedicationSnapshots(user.uid);
          setupTrackingDataSnapshot(user.uid);
        } else {
          setSubscriptionData(null);
          setListings(/** @type {Record<string, Record<string, unknown>>} */ ({}));
          setInvoices([]);
          setMedications([]);
          setReminderStatuses(new Map());
          setMedicationsLoading(false);
          clearTrackingData();
        }
      } else {
        if (unsubRef.current) {
          unsubRef.current();
          unsubRef.current = null;
        }
        detachPatientScopedFirestoreListeners();
        setSubscription(null);
        setSubscriptionData(null);
        setUserData(null);
        setDailyPass(null);
        setListings(/** @type {Record<string, Record<string, unknown>>} */ ({}));
        setInvoices([]);
        // Clear medication cache
        setMedications([]);
        setReminderStatuses(new Map());
        setMedicationsLoading(false);
        // Clear tracking data cache
        clearTrackingData();
      }
      setLoading(false);
    });

    // Unconditional 10s safety so we never block splash forever (e.g. iOS when auth never fires)
    const safetyTimeoutId = setTimeout(() => setLoading(false), 10000);

    return () => {
      clearTimeout(safetyTimeoutId);
      authListener();
      if (unsubRef.current) unsubRef.current();
      if (dailyPassUnsubRef.current) dailyPassUnsubRef.current();
      if (subscriptionUnsubRef.current) subscriptionUnsubRef.current();
      if (listingsUnsubRef.current) listingsUnsubRef.current();
      if (invoicesUnsubRef.current) invoicesUnsubRef.current();
      if (medicationUnsubRef.current) medicationUnsubRef.current();
      if (reminderUnsubRef.current) reminderUnsubRef.current();
      if (trackingUnsubRef.current) trackingUnsubRef.current();
    };
  }, [clearTrackingData]);

  const handleLogout = async () => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
    if (dailyPassUnsubRef.current) {
      dailyPassUnsubRef.current();
      dailyPassUnsubRef.current = null;
    }
    detachPatientScopedFirestoreListeners();

    // Clear medication cache
    setMedications([]);
    setReminderStatuses(new Map());
    setMedicationsLoading(false);
    // Clear tracking data cache
    clearTrackingData();

    await firebase.auth().signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        subscription, // This is the user ROLE (patient, facility, etc.) - confusing naming for backwards compatibility
        subscriptionData, // This is the actual Stripe subscription object with status

        userData,
        listings,
        invoices,
        userLoading: loading,
        logout: handleLogout,

        // Medication management
        medications,
        reminderStatuses,
        medicationsLoading,
        addMedication,
        updateMedication,
        deleteMedication,
        refreshReminderStatuses,
        loadMedications,

        // Tracking data management
        trackingData,
        loadTrackingData,
        clearTrackingData,
        forceResetTrackingData, // DEBUG function
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
